import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { setUser } from '@/lib/sentry';
import { trackLogin, trackSignup, setAnalyticsUser, clearAnalyticsUser } from '@/lib/analytics';
import { useThemeStore } from './themeStore';
import type { Profile } from '@/types';
import type { Session } from '@supabase/supabase-js';

const REMEMBER_ME_KEY = 'erh_remember_me';
const FAILED_ATTEMPTS_KEY = 'erh_failed_login_attempts';
const LOCKOUT_UNTIL_KEY = 'erh_lockout_until';
const MAX_ATTEMPTS = 5;
const LOCKOUT_MINUTES = 10;

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  lockoutUntil: number | null;
  failedAttempts: number;

  initialize: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, email?: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchProfile: (userId: string) => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  clearError: () => void;
  setRememberMe: (remember: boolean) => void;
}

const getLockoutUntil = () => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(LOCKOUT_UNTIL_KEY);
  return raw ? Number(raw) : null;
};

const getFailedAttempts = () => {
  if (typeof window === 'undefined') return 0;
  const raw = localStorage.getItem(FAILED_ATTEMPTS_KEY);
  return raw ? Number(raw) : 0;
};

const setLockout = (until: number | null) => {
  if (typeof window === 'undefined') return;
  if (until) {
    localStorage.setItem(LOCKOUT_UNTIL_KEY, String(until));
  } else {
    localStorage.removeItem(LOCKOUT_UNTIL_KEY);
  }
};

const setFailedAttempts = (count: number) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(FAILED_ATTEMPTS_KEY, String(count));
};

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  profile: null,
  loading: true,
  error: null,
  lockoutUntil: getLockoutUntil(),
  failedAttempts: getFailedAttempts(),

  initialize: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      set({ session, loading: false, lockoutUntil: getLockoutUntil(), failedAttempts: getFailedAttempts() });

      if (session?.user) {
        await get().fetchProfile(session.user.id);
      }

      supabase.auth.onAuthStateChange(async (_event, session) => {
        set({ session });
        if (session?.user) {
          await get().fetchProfile(session.user.id);
        } else {
          set({ profile: null });
        }
      });
    } catch {
      set({ loading: false, error: 'Failed to initialize auth' });
    }
  },

  login: async (username: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const lockoutUntil = getLockoutUntil();
      if (lockoutUntil && Date.now() < lockoutUntil) {
        const remaining = Math.ceil((lockoutUntil - Date.now()) / 60000);
        throw new Error(`Too many attempts. Try again in ${remaining} minute(s).`);
      }

      const email = `${username.toLowerCase()}@internal.erh`;
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      // Set session immediately from response (don't wait for onAuthStateChange)
      if (data.session) {
        set({ session: data.session });
      }

      setFailedAttempts(0);
      setLockout(null);
      set({ failedAttempts: 0, lockoutUntil: null });
      trackLogin();

      // Fetch profile immediately so Desktop can check pill_choice
      if (data.session?.user) {
        await get().fetchProfile(data.session.user.id);
      }
    } catch (err: unknown) {
      const attempts = getFailedAttempts() + 1;
      setFailedAttempts(attempts);
      let lockoutUntil: number | null = null;

      if (attempts >= MAX_ATTEMPTS) {
        lockoutUntil = Date.now() + LOCKOUT_MINUTES * 60 * 1000;
        setLockout(lockoutUntil);
      }

      const message = err instanceof Error ? err.message : 'Login failed';
      set({ error: message, failedAttempts: attempts, lockoutUntil });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  register: async (username: string, password: string, email?: string) => {
    set({ loading: true, error: null });
    try {
      const internalEmail = `${username.toLowerCase()}@internal.erh`;
      const { data, error } = await supabase.auth.signUp({
        email: internalEmail,
        password,
        options: {
          data: {
            username,
            recovery_email: email || null,
          },
        },
      });
      if (error) throw error;

      // Set session immediately from signup response (auto-confirm is on)
      if (data.session) {
        set({ session: data.session });
        if (data.session.user) {
          await get().fetchProfile(data.session.user.id);
        }
      }

      trackSignup();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      set({ error: message });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  logout: async () => {
    await supabase.auth.signOut();

    if (typeof window !== 'undefined') {
      localStorage.removeItem(REMEMBER_ME_KEY);
      localStorage.removeItem(FAILED_ATTEMPTS_KEY);
      localStorage.removeItem(LOCKOUT_UNTIL_KEY);
      sessionStorage.clear();
      setUser(null);
      clearAnalyticsUser();
      useThemeStore.getState().resetRabbitHole();
    }

    set({ session: null, profile: null, lockoutUntil: null, failedAttempts: 0 });
  },

  fetchProfile: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      set({ profile: data as Profile });

      if (data) {
        setUser({
          id: data.id,
          username: data.username,
        });
        setAnalyticsUser(data.id);

        // Auto-sync rabbit hole state for returning red-pill members
        if (data.pill_choice === 'red') {
          useThemeStore.getState().enterRabbitHole();
        }
      }
    } catch {
      console.error('Failed to fetch profile');
    }
  },

  updateProfile: async (updates: Partial<Profile>) => {
    const { profile } = get();
    if (!profile) throw new Error('No profile loaded');

    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id);

      if (error) throw error;
      set({ profile: { ...profile, ...updates } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Update failed';
      set({ error: message });
      throw err; // propagate so callers know it failed
    } finally {
      set({ loading: false });
    }
  },

  clearError: () => set({ error: null }),

  setRememberMe: (remember: boolean) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(REMEMBER_ME_KEY, String(remember));
  },
}));
